import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch } from '@fortawesome/free-solid-svg-icons/faSearch';

import Autocomplete, { Section } from 'buyside/components/presentation/Autocomplete/autocomplete';
import Link from 'buyside/components/presentation/Link/link';

import DownArrowIcon from 'buyside/svgs/down_arrow.svg';
import SearchIcon from 'buyside/svgs/search.svg';
import UpArrowIcon from 'buyside/svgs/up_arrow.svg';

import {
  allocatorProfileUrl,
  employerProfileVanityUrl,
  entityUrl,
  newIdeaUrl,
  podcastEpisodeIndexUrl,
  podcastEpisodeShowUrl,
  profileSearchUrl,
  profileUrl,
  userIdeaUrl,
  videoIndexUrl,
  videoShowUrl,
  ideaSearchUrl,
  ideaUrl
} from 'buyside/util/urlHelper';

import { Maybe, OmnisearchQuery, useOmnisearchLazyQuery } from 'interfaces/graphql-typings';

export enum SuggestionCategory {
  ALLOCATORS = 'allocators',
  EMPLOYERS = 'employers',
  ENTITIES = 'entities',
  FUNDS = 'funds',
  IDEAS = 'ideas',
  PEOPLE = 'people',
  PODCAST_EPISODES = 'podcast_episodes',
  VIDEOS = 'videos'
}

export type Suggestion = Maybe<{
  category: SuggestionCategory;
  id: string;
  label: string | JSX.Element;
  path: string;
  valueToRender?: string;
}>;

import styles from 'buyside/components/presentation/Omnisearch/omnisearchStyles.scss';

const Omnisearch: React.FC<{ canWriteIdeas?: boolean }> = ({ canWriteIdeas }) => {
  const [suggestions, setSuggestions] = useState<Maybe<Section>[]>([]);

  const [search, { loading, error }] = useOmnisearchLazyQuery();

  const updateAllocators = (
    allocators: OmnisearchQuery['omnisearch']['allocators'],
    searchTerm: string
  ): Maybe<Section> => {
    if (!allocators?.length) return null;

    return {
      showLink: allocators.length > 5,
      suggestions: allocators
        .slice(0, 5)
        .map(allocator => {
          if (!allocator) return null;
          return {
            label: allocator.name || '',
            id: allocator.id,
            path: allocatorProfileUrl(allocator.id),
            category: SuggestionCategory.ALLOCATORS
          };
        })
        .filter(Boolean),
      title: 'Allocators',
      url: `/pro/cap-intro/allocators?text=${searchTerm}`
    };
  };

  const updateEmployers = (
    employers: OmnisearchQuery['omnisearch']['employers'],
    searchTerm: string
  ): Maybe<Section> => {
    if (!employers?.length) return null;

    return {
      showLink: employers.length > 5,
      suggestions: employers
        .slice(0, 5)
        .map(employer => {
          if (!employer) return null;
          return {
            label: employer.name,
            id: employer.id,
            path: profileSearchUrl({
              employer: employer.name,
              employer_id: employer.id
            }),
            category: SuggestionCategory.EMPLOYERS
          };
        })
        .filter(Boolean),
      title: 'Employers',
      url: profileSearchUrl({ employer: searchTerm })
    };
  };

  const updateEntities = (entities: OmnisearchQuery['omnisearch']['entities']): Maybe<Section> => {
    if (!entities?.length) return null;

    return {
      showLink: false,
      suggestions: [
        ...entities.slice(0, 5).map(entity => {
          if (!entity) return null;
          return {
            label: `${entity.primarySymbol ? entity.primarySymbol : ''} ${entity.name}`,
            id: entity.id,
            path: entityUrl(entity.id),
            category: SuggestionCategory.ENTITIES
          };
        }),
        canWriteIdeas
          ? {
              label: (
                <>
                  <FontAwesomeIcon className={styles.searchIcon} icon={faSearch} /> Search more
                  entities to write about
                </>
              ),
              id: '',
              path: newIdeaUrl(),
              category: SuggestionCategory.ENTITIES,
              valueToRender: 'Search more entities'
            }
          : null
      ].filter(Boolean),
      title: 'Entities'
    };
  };

  const updateFunds = (
    funds: OmnisearchQuery['omnisearch']['funds'],
    searchTerm: string
  ): Maybe<Section> => {
    if (!funds?.length) return null;

    return {
      showLink: funds.length > 5,
      suggestions: funds
        .slice(0, 5)
        .map(fund => {
          if (!fund) return null;
          return {
            label: fund.name || '',
            id: fund.id,
            path: employerProfileVanityUrl(fund.id),
            category: SuggestionCategory.FUNDS
          };
        })
        .filter(Boolean),
      title: 'Funds',
      url: `/pro/cap-intro/funds?text=${searchTerm}`
    };
  };

  const updateUsers = (
    anonUsers: OmnisearchQuery['omnisearch']['anonUsers'],
    users: OmnisearchQuery['omnisearch']['users'],
    searchTerm: string
  ): Maybe<Section> => {
    const mergedUsers = [...anonUsers.slice(0, 5), ...users.slice(0, 5)];

    if (!mergedUsers.length) return null;

    return {
      showLink: mergedUsers.length > 5,
      suggestions: mergedUsers
        .map(user => {
          if (user.__typename === 'AnonUser' && user.username) {
            return {
              label: <span>{user.username}</span>,
              valueToRender: user.username,
              id: user.id,
              path: userIdeaUrl(encodeURI(user.username)),
              category: SuggestionCategory.PEOPLE
            };
          }

          if (user.__typename === 'User') {
            const companyString = user.profile?.companyString;

            return {
              label: (
                <span>
                  {user.displayName}
                  <em>{companyString ? ` - ${companyString}` : ''}</em>
                </span>
              ),
              valueToRender: user.displayName,
              id: user.id,
              path: user.profile ? profileUrl(user.profile.id) : '',
              category: SuggestionCategory.PEOPLE
            };
          }

          return null;
        })
        .filter(Boolean),
      title: 'People',
      url: profileSearchUrl({ name: searchTerm })
    };
  };

  const updateVideos = (videos: OmnisearchQuery['omnisearch']['videos']): Maybe<Section> => {
    if (!videos?.length) return null;

    return {
      showLink: videos.length > 5,
      suggestions: videos.slice(0, 5).map(({ title, id, vanityLink }) => {
        if (!title) return null;

        return {
          label: <p className={styles.truncatedSuggestion}>{title}</p>,
          id,
          path: videoShowUrl(vanityLink || id),
          category: SuggestionCategory.VIDEOS,
          valueToRender: title
        };
      }),
      title: 'Videos',
      url: videoIndexUrl()
    };
  };

  const updatePodcastEpisodes = (
    podcastEpisodes: OmnisearchQuery['omnisearch']['podcastEpisodes']
  ): Maybe<Section> => {
    if (!podcastEpisodes.length) return null;

    return {
      showLink: podcastEpisodes.length > 5,
      suggestions: podcastEpisodes.slice(0, 5).map(({ id, name, vanityLink }) => {
        return {
          label: <p className={styles.truncatedSuggestion}>{name}</p>,
          id,
          path: podcastEpisodeShowUrl(vanityLink || id),
          category: SuggestionCategory.PODCAST_EPISODES,
          valueToRender: name
        };
      }),
      title: 'Podcast Episodes',
      url: podcastEpisodeIndexUrl()
    };
  };

  const updateIdeas = (
    ideas: OmnisearchQuery['omnisearch']['ideas'],
    searchTerm: string
  ): Maybe<Section> => {
    if (!ideas.length) return null;

    return {
      showLink: ideas.length > 5,
      suggestions: ideas
        .slice(0, 5)
        .map(({ id, assetSymbol, direction, elevatorPitch, entityName, phraseCount }) => {
          return {
            label: (
              <div className={styles.ideaSuggestion}>
                <span className={styles.ideaSuggestionTitle}>
                  {direction === 'long' ? <UpArrowIcon data-direction="long" /> : null}
                  {direction === 'short' ? <DownArrowIcon data-direction="short" /> : null}
                  <strong>
                    {entityName}
                    {assetSymbol ? ` (${assetSymbol})` : ''}
                  </strong>
                </span>
                <em className={styles.truncatedSuggestion}>{elevatorPitch}</em>
                <div className={styles.ideaSuggestionPhraseCount}>
                  <span>
                    <strong>"{searchTerm}"</strong> appears {phraseCount}{' '}
                    {phraseCount === 1 ? 'time' : 'times'} in this Idea.
                  </span>
                </div>
              </div>
            ),
            id,
            path: ideaUrl(id),
            category: SuggestionCategory.IDEAS,
            valueToRender: entityName
          };
        }),
      title: 'Ideas',
      url: ideaSearchUrl(`text=${searchTerm}`)
    };
  };

  return (
    <div className={styles.wrapper}>
      <Autocomplete
        suggestions={suggestions}
        changeIntercept={(searchTerm: string, reason: string): void => {
          if (reason === 'input-changed') {
            search({ variables: { query: searchTerm } }).then(result => {
              if (result.data?.omnisearch) {
                setSuggestions(
                  [
                    updateEntities(result.data.omnisearch.entities),
                    updateIdeas(result.data.omnisearch.ideas, searchTerm),
                    updateUsers(
                      result.data.omnisearch.anonUsers,
                      result.data.omnisearch.users,
                      searchTerm
                    ),
                    updateVideos(result.data.omnisearch.videos),
                    updatePodcastEpisodes(result.data.omnisearch.podcastEpisodes),
                    updateEmployers(result.data.omnisearch.employers, searchTerm),
                    updateFunds(result.data.omnisearch.funds, searchTerm),
                    updateAllocators(result.data.omnisearch.allocators, searchTerm)
                  ].filter(Boolean)
                );
              }
            });
          }
        }}
        selectIntercept={(_suggestionValue, suggestion): void => location.assign(suggestion.path)}
        formatSectionTitle={(section): React.ReactElement => (
          <div className={styles.sectionTitle} onMouseDown={(e): void => e.preventDefault()}>
            <span>
              <strong>{section.title}</strong>
            </span>
            {section.showLink ? <Link to={section.url}>Show More</Link> : null}
          </div>
        )}
        getSuggestionValue={(suggestion): string =>
          suggestion.valueToRender ? suggestion.valueToRender : suggestion.label
        }
        inputProps={{ placeholder: 'Search SumZero' }}
        loading={loading}
        error={error}
        forOmnisearch
        debounce
        autosuggestOptions={{
          focusInputOnSuggestionClick: false,
          id: 'omnisearch',
          multiSection: true
        }}
      />
      <SearchIcon className={styles.searchIcon} />
    </div>
  );
};

export default Omnisearch;
